import { useArenaSeoHead } from '@/lib/arena-seo-head'
import { useI18n } from '@/lib/i18n'
import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'

type GuideKind = 'rules' | 'roles'

const GUIDE_FILES: Record<GuideKind, string> = {
  rules: 'GENERAL_RULES',
  roles: 'ROLE_INDEX',
}

interface LoadedGuide {
  language: string
  kind: GuideKind
  markdown: string
}

export function ArenaGuidePage({ kind }: { kind: GuideKind }) {
  const { language, t } = useI18n()
  const [loadedGuide, setLoadedGuide] = useState<LoadedGuide | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const contentReady = loadedGuide?.language === language
    && loadedGuide.kind === kind
  useArenaSeoHead(kind, contentReady)

  useEffect(() => {
    const controller = new AbortController()
    setLoadFailed(false)
    const markdownUrl = `/rules/${language}/${GUIDE_FILES[kind]}.md`
    fetch(markdownUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok)
          throw new Error(`Guide unavailable: ${response.status}`)
        return response.text()
      })
      .then(markdown => setLoadedGuide({ language, kind, markdown }))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          console.error('[arena] guide load failed', error)
          setLoadFailed(true)
        }
      })

    return () => controller.abort()
  }, [language, kind])

  const hasServerPrelude = typeof document !== 'undefined'
    && document.getElementById('seo-arena-guide-prelude') !== null
  if (!contentReady && hasServerPrelude)
    return null

  return (
    <main className="mx-auto min-h-dvh max-w-4xl px-4 pb-20 pt-24 text-start sm:px-6 lg:px-8">
      {contentReady
        ? (
            <article className="custom-rules-prose prose prose-slate max-w-none text-start dark:prose-invert prose-h2:text-lg prose-li:my-1 md:prose-h2:text-xl">
              <ReactMarkdown>{loadedGuide.markdown}</ReactMarkdown>
            </article>
          )
        : (
            <p className="py-8 text-center text-muted-foreground">
              {loadFailed ? t('game.rulesLoadFailed') : t('leaderboard.loadingData')}
            </p>
          )}
    </main>
  )
}
